import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsReportEmployeeScope } from '~/data/reports';

export type ReportEmployeeScopeOption = {
  value: AnalyticsReportEmployeeScope;
  label: string;
  description?: string;
};

type UseReportEmployeeScopeSelectorOptionsOutput = {
  employeeScopeOptions: ReportEmployeeScopeOption[];
  getEmployeeScopeOption: (
    employeeScope?: AnalyticsReportEmployeeScope,
  ) => ReportEmployeeScopeOption | undefined;
};

export const useReportEmployeeScopeSelectorOptions =
  (): UseReportEmployeeScopeSelectorOptionsOutput => {
    const { t } = useTranslation();

    const employeeScopeOptions = useMemo((): ReportEmployeeScopeOption[] => {
      return [
        {
          value: AnalyticsReportEmployeeScope.CurrentEmployees,
          // NOTE Includes ACTIVE, PASSIVE
          label: t('laCurrentEmployees'),
          description: t('employeeScope_currentEmployees_description'),
        },
        {
          value: AnalyticsReportEmployeeScope.AllEmployees,
          // NOTE Includes: ACTIVE, PASSIVE, ENDED
          label: t('laAllEmployees'),
          description: t('employeeScope_allEmployees_description'),
        },
        {
          value: AnalyticsReportEmployeeScope.ActiveEmployees,
          // NOTE Includes ACTIVE
          label: t('laActiveEmployees'),
          description: t('employeeScope_activeEmployees_description'),
        },
        {
          value: AnalyticsReportEmployeeScope.EndedEmployees,
          // NOTE Includes ENDED
          label: t('laEndedEmployees'),
          description: t('employeeScope_endedEmployees_description'),
        },
        {
          value: AnalyticsReportEmployeeScope.NewEmployees,
          // NOTE Includes NEW
          label: t('laNewEmployees'),
          description: t('employeeScope_newEmployees_description'),
        },
        {
          value: AnalyticsReportEmployeeScope.ProvisionalEmployees,
          // NOTE Includes PENDING
          label: t('employeeScope_provisionalEmployees'),
          description: t('employeeScope_provisionalEmployees_description'),
        },
        {
          value: AnalyticsReportEmployeeScope.SystemEmployees,
          // NOTE Includes NEW, ACTIVE, PASSIVE, ENDED
          label: t('employeeScope_systemEmployees'),
          description: t('employeeScope_systemEmployees_description'),
        },
      ];
    }, [t]);

    const getEmployeeScopeOption = useCallback(
      (
        employeeScope?: AnalyticsReportEmployeeScope,
      ): ReportEmployeeScopeOption | undefined => {
        return !!employeeScope
          ? employeeScopeOptions.find((o) => o.value === employeeScope)
          : undefined;
      },
      [employeeScopeOptions],
    );

    return {
      employeeScopeOptions,
      getEmployeeScopeOption,
    };
  };
